import { FC, useEffect, useRef, useState } from 'react'
import './pledge-slot-field-multiple.css'

// COMPONENTS
import * as Icons from '../../../_constants/icons'
import Avatar from '../avatar'
import CurrencyTextField from '@components/molecules/currency-textfield'
import TextField from '@components/atoms/textfield'

// UTILITIES
import { max } from '@constants'
import { flatten } from '@utils/string-helper'
import { priceFormatter, toPennies } from '@utils/number-helper'
import { toUSD, useCurrencyInput, useInput } from '@hooks/inputs'
import Button from '@components/atoms/button'


interface Props extends React.HTMLAttributes<HTMLFieldSetElement> {
    addedPledge: boolean,
    group: Group
    pledge: Pledge
    pledgeSlots: Pledge[]
    hidePledge: boolean,
    showRemove: boolean,
    onPledgeEdit: (editing: boolean) => void
    onPledgeChange: (name: string, amount: number) => void
    onPledgeRemove?: () => void
}

const PledgeSlotFieldsMultiple: FC<Props> = (props) => {
    const { className: classProps, addedPledge, group, pledge, pledgeSlots, hidePledge, showRemove, onPledgeEdit, onPledgeChange, onPledgeRemove, ...fieldsetProps } = props
    const className = flatten("pledge-slot-fields-multiple", classProps)
    const [action, setAction] = useState(false)
    const [isEdit, setIsEdit] = useState(false)
    const amountInputRef = useRef<HTMLInputElement>(null)

    const [name, pledgeNameProps] = useInput({
        initValue: pledge.name,
        onChange: (value) => {
            onPledgeChange(value, toPennies(amount))
            return value
        },
        onBlur: () => onPledgeChange(name, toPennies(amount))
    })

    const [amount, pledgeAmountProps, setPledgeAmount] = useCurrencyInput({
        initValue: !!pledge.amount ? toUSD(`${(pledge.amount) / 100}`, undefined, undefined, true) : '0', max,
    })

    useEffect(() => {
        onPledgeEdit(isEdit)
    }, [isEdit])

    function handleAdd() {
        if ((name || amount) || showRemove) return
        setAction(true)
    }

    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
        const amount = e.target.value
        setPledgeAmount(amount)
        onPledgeChange(name, toPennies(amount))
    };

    function handleRemove() {
        !!onPledgeRemove && onPledgeRemove()
    }

    if (hidePledge) return <></>
    else return (
        <fieldset name="pledge" className={className} {...fieldsetProps}>
            {(addedPledge && !isEdit) ? (<>
                <input onChange={() => { }} name="name" value={name} hidden />
                <input onChange={() => { }} name="amount" value={amount} hidden />
                <div className='flex flex-row items-center justify-between'>
                    <div className='flex flex-row items-center gap-2'>
                        <Avatar slotName={name} />
                        <p className='text-body text-gray-500'>{name}</p>
                    </div>
                    <div className='flex flex-col items-end'>
                        <h4 className='text-body'>{priceFormatter.format(toPennies(amount) / 100)}</h4>
                        <a className="text-body-2 text-blue-500" onClick={() => setIsEdit(true)}>Edit</a>
                    </div>
                </div>
            </>
            ) : (<>
                {((name || amount) || showRemove || action) &&
                    <div className="remove" onClick={handleRemove}>
                        <Icons.Close height="10px" width="10px" tint="var(--splitcard-black)" strokeWidth="3" />
                    </div>
                }
                <div className='input-row'>
                    <TextField
                        className='name-input'
                        {...pledgeNameProps}
                        onClick={handleAdd}
                        placeholder={'Enter Name'}
                        name="name"
                        enterKeyHint="next"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault()
                                e.stopPropagation()
                                // Focus synchronously to maintain iOS user gesture chain
                                amountInputRef.current?.focus()
                            }
                        }}
                    />
                    <CurrencyTextField
                        className='amount-input'
                        ref={amountInputRef}
                        {...pledgeAmountProps}
                        onClick={handleAdd}
                        onChange={handleChange}
                        name="amount"
                    />
                </div>
                {isEdit &&
                    <Button className='flex flex-row items-center justify-center gap-2' theme='dark' style={{ background: 'var(--splitcard-green-600)' }} type='button' onClick={() => setIsEdit(false)} disabled={!amount}>
                        <Icons.CheckSmall type="bordered" tint={'var(--splitcard-white)'} width="1em" height="1em" />
                        Confirm
                    </Button>
                }
            </>)}
        </fieldset>
    )
}

export default PledgeSlotFieldsMultiple
